import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import AdminLayout from '@/components/AdminLayout'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  PauseCircle,
  Search,
  Download,
  FileText,
  MoreHorizontal,
} from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { supabase } from '@/integrations/supabase/client'
import TestAssignmentForm from '@/components/TestAssignmentForm'
import { getPaginationPages, getTotalPages, paginate } from '@/utils/pagination'
import { useToast } from '@/hooks/use-toast'
import EmailOptionsModal, { EmailOptions } from '@/components/EmailOptionsModal'
import EmailContentModal, { EmailContent } from '@/components/EmailContentModal'
import EmailPreviewModal from '@/components/EmailPreviewModal'
import EmailSendingProgressModal, { EmailProgress } from '@/components/EmailSendingProgressModal'
import { API_BASE_URL, authedFetch } from '@/utils/apiConfig'
import { Checkbox } from '@/components/ui/checkbox'
import {
  emailStatusFilterOptions,
  getEmailStatusColor,
  getEmailStatusIcon,
  getEmailStatusText,
  getStatusColor,
  getStatusIcon,
  getStatusText,
  statusFilterOptions,
} from '@/utils/status'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import * as XLSX from 'xlsx'
import { Dialog, DialogContent, DialogDescription, DialogHeader } from '@/components/ui/dialog'
import { DialogTitle } from '@radix-ui/react-dialog'
import EmailArchiveModal from '@/components/EmailArchiveModal'
import { Label } from '@/components/ui/label'
import { formatLocalDate } from '@/utils/dateUtils'
import { fetchAllRows } from '@/utils/supabasePagination'

const AdminTestDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [test, setTest] = useState<any>(null)
  const [questions, setQuestions] = useState<any[]>([])
  const [assignments, setAssignments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [isAssigning, setIsAssigning] = useState(false)
  const [questionsPage, setQuestionsPage] = useState(1)
  const [assignmentsPage, setAssignmentsPage] = useState(1)

  const [showDueDateModal, setShowDueDateModal] = useState(false)
  const [newDueDate, setNewDueDate] = useState<Date | null>(null)
  const [updatingDueDate, setUpdatingDueDate] = useState(false)

  // EMAIL related state
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([])
  const [showEmailOptionsModal, setShowEmailOptionsModal] = useState(false)
  const [showEmailContentModal, setShowEmailContentModal] = useState(false)
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false)
  const [showEmailProgressModal, setShowEmailProgressModal] = useState(false)
  const [emailContent, setEmailContent] = useState<EmailContent>({
    subject: '',
    content: '',
    body: '',
  })
  const [emailProgress, setEmailProgress] = useState<EmailProgress>({
    status: 'sending',
    currentStep: '',
    totalEmails: 0,
    sentEmails: 0,
    failedEmails: [],
    successEmails: [],
  })
  const [composedEmail, setComposedEmail] = useState<{
    subject: string
    content: string
    html?: string
    designJson?: any
  } | null>(null)
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('')
  const [emailStatusFilter, setEmailStatusFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')

  // Email archive modal state
  const [showEmailArchiveModal, setShowEmailArchiveModal] = useState(false)
  const [selectedEmailArchive, setSelectedEmailArchive] = useState<any>(null)

  const QUESTIONS_PER_PAGE = 5
  const ASSIGNMENTS_PER_PAGE = 8

  // Fetch assignments only
  const fetchAssignments = async () => {
    const data = await fetchAllRows<any>(
      'employee_tests',
      `id, status, attempt_count, started_at, completed_at, pairing_code,
       assignment_start_date, assignment_due_date, created_at, email_status,
       employee:employee_id ( personel, mail_adresi, sicil )`,
      (q) => q.eq('test_id', id).neq('status', 'renewed')
    )
    setAssignments(data)
  }

  const handleAssignComplete = () => {
    setIsAssigning(false)
    fetchAssignments()
  }

  // Fetch all data (test, questions, assignments) - initial load
  const fetchAll = async () => {
    setLoading(true)
    // 1. Fetch test info
    const { data: testData } = await supabase.from('tests').select('*').eq('id', id).single()
    setTest(testData)

    let fetchedQuestions = []
    // 2. Get questions according to test type
    if (testData) {
      let manualQuestions = []
      let randomCategoryQuestions = []
      if (testData.type === 'manual' || testData.type === 'hybrid') {
        // Get questions from test_questions (manual assigned)
        const { data: tqRows } = await supabase
          .from('test_questions')
          .select('question_id')
          .eq('test_id', id)
        const manualIds = tqRows ? tqRows.map((r) => r.question_id) : []
        if (manualIds.length > 0) {
          const { data: manualQs } = await supabase
            .from('questions')
            .select('id,text,category,created_at,choices:choices(id,label,text,is_correct)')
            .in('id', manualIds)
          manualQuestions = (manualQs || []).map((question) => ({
            ...question,
            choices:
              question.choices?.sort((a: any, b: any) => {
                return (a.label || a.id).localeCompare(b.label || b.id)
              }) || [],
          }))
        }
      }
      if (testData.type === 'random' || testData.type === 'hybrid') {
        const categoriesObj = testData.categories || {}
        const categories = Object.keys(categoriesObj)
        if (categories.length > 0) {
          const { data: catQs } = await supabase
            .from('questions')
            .select('id,text,category,created_at,choices:choices(id,label,text,is_correct)')
            .in('category', categories)
          randomCategoryQuestions = (catQs || []).map((question) => ({
            ...question,
            choices:
              question.choices?.sort((a: any, b: any) => {
                return (a.label || a.id).localeCompare(b.label || b.id)
              }) || [],
          }))
        }
      }
      if (testData.type === 'hybrid') {
        const allQs = [...manualQuestions, ...randomCategoryQuestions]
        const uniqueQs = allQs.filter(
          (q, idx, arr) => arr.findIndex((qq) => qq.id === q.id) === idx
        )
        fetchedQuestions = uniqueQs
      } else if (testData.type === 'manual') {
        fetchedQuestions = manualQuestions
      } else if (testData.type === 'random') {
        fetchedQuestions = randomCategoryQuestions
      }
    }
    setQuestions(fetchedQuestions)
    await fetchAssignments()
    setLoading(false)
  }

  // Initial load
  useEffect(() => {
    if (!id) return
    fetchAll()
    // eslint-disable-next-line
  }, [id])

  // --- Email logic ---
  const handleSelectEmployee = (assignmentId: string) => {
    setSelectedEmployees((prev) =>
      prev.includes(assignmentId)
        ? prev.filter((id) => id !== assignmentId)
        : [...prev, assignmentId]
    )
  }

  // Email Modal Stepper
  const handleEmailOptions = (options: EmailOptions) => {
    if (options.sendEmail) {
      setShowEmailContentModal(true)
    } else {
      toast({
        title: 'İşlem Tamamlandı',
        description: 'Email gönderilmeden işlem tamamlandı.',
      })
    }
    setShowEmailOptionsModal(false)
  }
  const handleEmailContentBack = () => {
    setShowEmailContentModal(false)
    setShowEmailOptionsModal(true)
  }

  const handleEmailPreview = (content: EmailContent) => {
    setEmailContent({
      subject: content.subject,
      content: content.content,
      body: content.body,
      html: content.html,
      designJson: content.designJson,
    })

    setComposedEmail({
      subject: content.subject,
      content: content.content,
      html: content.html,
      designJson: content.designJson,
    })

    setShowEmailPreviewModal(true)
    setShowEmailContentModal(false)
  }

  const handleEmailPreviewBack = () => {
    setShowEmailPreviewModal(false)
    setShowEmailContentModal(true)
  }

  // Email gönderimi
  const handleSendEmails = async () => {
    setShowEmailPreviewModal(false)
    setShowEmailProgressModal(true)

    const selectedAssignments = assignments.filter((a) => selectedEmployees.includes(a.id))

    const recipients = selectedAssignments.map((a) => ({
      assignment_id: a.id,
      email: a.employee?.mail_adresi,
      code: a.pairing_code,
      name: a.employee?.personel,
      test_name: test.name,
      start_date: formatLocalDate(a.assignment_start_date),
      due_date: formatLocalDate(a.assignment_due_date),
      login_link: 'https://mavi.novaforge.tech/video-interview',
      time_limit: test.time_limit,
    }))

    setEmailProgress({
      status: 'sending',
      currentStep: 'Email gönderimi başlatıldı...',
      totalEmails: recipients.length,
      sentEmails: 0,
      failedEmails: [],
      successEmails: [],
    })

    try {
      const res = await authedFetch(`${API_BASE_URL}/api/assignments/send_invitations`, {
        method: 'POST',
        body: JSON.stringify({
          recipients,
          subject: composedEmail?.subject,
          html: composedEmail?.html,
          design_json: composedEmail?.designJson,
        }),
      })
      const data = await res.json()
      setEmailProgress((prev) => ({
        ...prev,
        status: 'completed',
        currentStep: 'Tüm işlemler tamamlandı!',
        sentEmails: Array.isArray(data?.results?.sent) ? data.results.sent.length : 0,
        failedEmails: Array.isArray(data?.results?.failed)
          ? data.results.failed.map((e: any) => e.email || e)
          : [],
        successEmails: data?.results?.sent || [],
      }))
      fetchAssignments()
    } catch (err: any) {
      setEmailProgress((prev) => ({
        ...prev,
        status: 'error',
        currentStep: 'Email gönderiminde hata oluştu.',
        error: err.message || 'Beklenmeyen bir hata oluştu.',
        sentEmails: 0,
        failedEmails: recipients.map((r: any) => r.email),
        successEmails: [],
      }))
    }
  }

  const filteredAssignments = assignments.filter((a) => {
    const search = employeeSearchQuery.toLocaleLowerCase('tr')
    let matches = true

    if (employeeSearchQuery) {
      matches =
        (a.employee?.personel || '').toLocaleLowerCase('tr').includes(search) ||
        (a.employee?.sicil || '').toLocaleLowerCase('tr').includes(search) ||
        (a.employee?.mail_adresi || '').toLocaleLowerCase('tr').includes(search)
    }

    if (emailStatusFilter === 'sent' && a.email_status !== 'sent') matches = false
    if (emailStatusFilter === 'failed' && a.email_status !== 'failed') matches = false
    if (emailStatusFilter === 'not_sent' && a.email_status) matches = false

    if (statusFilter !== 'all' && a.status !== statusFilter) matches = false

    return matches
  })

  const totalQuestionsPages = getTotalPages(questions.length, QUESTIONS_PER_PAGE)
  const totalAssignmentsPages = getTotalPages(filteredAssignments.length, ASSIGNMENTS_PER_PAGE)
  const paginatedQuestions = paginate(questions, questionsPage, QUESTIONS_PER_PAGE)
  const paginatedAssignments = paginate(filteredAssignments, assignmentsPage, ASSIGNMENTS_PER_PAGE)

  const handleCloseEmailProgress = () => {
    setShowEmailProgressModal(false)
    setSelectedEmployees([])
  }

  useEffect(() => {
    setAssignmentsPage(1)
  }, [employeeSearchQuery])

  const handleExportEmployees = () => {
    if (!filteredAssignments.length) {
      toast({
        title: 'Dışa Aktarılamadı',
        description: 'Aktarılacak çalışan bulunamadı.',
        variant: 'destructive',
      })
      return
    }

    // Tabloya yazılacak veriler
    const excelData = filteredAssignments.map((a) => ({
      Personel: a.employee?.personel || '',
      Sicil: a.employee?.sicil || '',
      Email: a.employee?.mail_adresi || '',
      'Atanma Tarihi': a.assignment_start_date || '',
      'Son Tarih': a.assignment_due_date || '',
      'Email Durumu': getEmailStatusText(a.email_status),
      Durum: getStatusText(a.status),
      'Erişim Kodu': a.pairing_code,
    }))

    // Excel dosyasını oluştur
    const worksheet = XLSX.utils.json_to_sheet(excelData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Atanmış Çalışanlar')

    // Kolon genişlikleri
    worksheet['!cols'] = [
      { wch: 18 }, // Personel
      { wch: 12 }, // Sicil
      { wch: 28 }, // Email
      { wch: 21 }, // Atanma Tarihi
      { wch: 21 }, // Son Tarih
      { wch: 16 }, // Email Durumu
      { wch: 15 }, // Durum
      { wch: 15 }, // Erişim Kodu
    ]

    const filename = `atanmis_calisanlar_${test.name?.replace(/\s+/g, '_')}.xlsx`
    XLSX.writeFile(workbook, filename)

    toast({
      title: 'Excel indirildi',
      description: 'Atanmış çalışanlar başarıyla indirildi.',
    })
  }

  const handleUpdateDueDate = async () => {
    if (!newDueDate || selectedEmployees.length === 0) return
    setUpdatingDueDate(true)
    try {
      // Call the RPC function with all IDs at once
      const { data, error } = await supabase.rpc('batch_update_due_dates', {
        assignment_ids: selectedEmployees,
        new_due_date: newDueDate.toISOString(),
        change_expired_to_pending: true,
      })

      if (error) throw error

      toast({
        title: 'Başarılı',
        description: `${selectedEmployees.length} çalışanın son tarihi güncellendi.`,
      })
      setShowDueDateModal(false)
      setNewDueDate(null)
      setSelectedEmployees([])
      fetchAssignments()
    } catch (err: any) {
      console.error('Update error:', err)
      toast({
        title: 'Hata',
        description: err.message || 'Güncelleme başarısız.',
        variant: 'destructive',
      })
    } finally {
      setUpdatingDueDate(false)
    }
  }

  const filteredIds = React.useMemo(
    () => filteredAssignments.map((a) => a.id),
    [filteredAssignments]
  )

  const selectedOnFiltered = React.useMemo(
    () => selectedEmployees.filter((id) => filteredIds.includes(id)),
    [selectedEmployees, filteredIds]
  )

  const allFilteredChecked =
    selectedOnFiltered.length === filteredIds.length && filteredIds.length > 0
  const someFilteredChecked =
    selectedOnFiltered.length > 0 && selectedOnFiltered.length < filteredIds.length

  useEffect(() => {
    setSelectedEmployees((prev) => {
      const keep = new Set(filteredIds)
      const next = prev.filter((id) => keep.has(id))
      return next.length === prev.length ? prev : next
    })
  }, [filteredIds])

  // Add new function
  const handleViewEmailArchive = async (assignmentId: string) => {
    try {
      const response = await authedFetch(
        `${API_BASE_URL}/api/assignments/${assignmentId}/email-history`
      )
      const data = await response.json()

      if (data.emails && data.emails.length > 0) {
        setSelectedEmailArchive(data) // Pass the entire data object
        setShowEmailArchiveModal(true)
      } else {
        toast({
          title: 'Email bulunamadı',
          description: 'Bu atama için gönderilmiş email kaydı bulunamadı.',
          variant: 'destructive',
        })
      }
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Email geçmişi yüklenirken bir hata oluştu.',
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-gray-400 text-lg">Yükleniyor...</div>
      </AdminLayout>
    )
  }
  if (!test) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-bold mb-4">Sınav Bulunamadı</h2>
          <p className="text-gray-500 mb-6">Aradığınız sınav bulunamadı veya silinmiş olabilir.</p>
          <Button asChild>
            <Link to="/admin/tests">Sınavlara Dön</Link>
          </Button>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      {isAssigning ? (
        <div className="flex justify-center">
          <TestAssignmentForm
            test={test}
            onComplete={handleAssignComplete}
            onAssignmentsUpdated={fetchAssignments}
          />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Üstte başlık, açıklama ve üst butonlar */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold">{test.name}</h1>
              <p className="text-gray-500">{test.description}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="default" onClick={() => setIsAssigning(true)}>
                + Yeni Kişi Ata
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/tests')}>
                Sınav Listesine Dön
              </Button>
            </div>
          </div>
          {/* Sınav bilgileri */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Sınav Bilgileri</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Oluşturulma:</span>
                    <span className="text-sm">{test.created_at}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Soru Sayısı:</span>
                    <span>{questions.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Süre:</span>
                    <span>{test.time_limit ? Math.round(test.time_limit / 60) : 60} dk</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          <Tabs defaultValue="questions">
            <TabsList className="grid w-full md:w-auto grid-cols-2 md:inline-flex">
              <TabsTrigger value="questions">Sorular</TabsTrigger>
              <TabsTrigger value="assignments">Atanmış Çalışanlar</TabsTrigger>
            </TabsList>
            <TabsContent value="questions" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Sınav Soruları</CardTitle>
                  <CardDescription>
                    Bu sınavda toplam {questions.length} soru bulunmaktadır
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {questions.length > 0 ? (
                    <div className="space-y-4">
                      {paginatedQuestions.map((question, index) => (
                        <div key={question.id} className="border rounded-md p-4">
                          <div className="flex gap-3 items-start mb-4">
                            <div className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div
                                dangerouslySetInnerHTML={{ __html: question.text }}
                                className="prose prose-sm max-w-none prose-headings:my-2 prose-p:my-1 prose-ol:my-2 prose-ul:my-2 prose-li:my-0.5"
                              />
                            </div>
                            <span className="inline-flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0">
                              {question.category}
                            </span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                            {(question.choices || []).map((option) => (
                              <div
                                key={option.id}
                                className="flex items-center gap-2 border rounded-md p-2"
                              >
                                <div
                                  className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-medium flex-shrink-0
                                    ${option.is_correct ? 'bg-green-200 text-green-700 font-bold' : 'bg-gray-100'}`}
                                >
                                  {option.label}
                                </div>
                                <span>{option.text}</span>
                                {option.is_correct && (
                                  <span className="ml-2 text-xs text-green-700 font-bold">
                                    (Doğru)
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                      {/* Pagination */}
                      {totalQuestionsPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setQuestionsPage((p) => Math.max(p - 1, 1))}
                            disabled={questionsPage === 1}
                          >
                            Önceki
                          </Button>
                          {getPaginationPages(questionsPage, totalQuestionsPages).map(
                            (page, idx) =>
                              page === '...' ? (
                                <span key={'ellipsis-q-' + idx} className="px-2 text-gray-400">
                                  ...
                                </span>
                              ) : (
                                <Button
                                  key={'page-q-' + page}
                                  variant={questionsPage === page ? 'default' : 'outline'}
                                  size="sm"
                                  onClick={() => setQuestionsPage(page)}
                                >
                                  {page}
                                </Button>
                              )
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setQuestionsPage((p) => Math.min(p + 1, totalQuestionsPages))
                            }
                            disabled={questionsPage === totalQuestionsPages}
                          >
                            Sonraki
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500">Bu sınav için henüz soru eklenmemiş.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            <TabsContent value="assignments" className="mt-6">
              <Card>
                <CardHeader className="!pb-4 border-b bg-white rounded-t-lg">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-col gap-1">
                      <CardTitle className="text-xl md:text-2xl font-bold">
                        Atanmış Çalışanlar
                      </CardTitle>
                      <CardDescription>
                        {employeeSearchQuery ||
                        emailStatusFilter !== 'all' ||
                        statusFilter !== 'all'
                          ? ` (${filteredAssignments.length} sonuç gösteriliyor)`
                          : ''}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2 flex-shrink-0 w-full md:w-auto">
                      <Button
                        variant="outline"
                        className="flex items-center gap-2 font-medium px-3"
                        onClick={handleExportEmployees}
                      >
                        <Download className="h-4 w-4" />
                        Dışarı Aktar
                      </Button>
                      <Button
                        variant="secondary"
                        className="flex items-center gap-2 font-medium px-3"
                        disabled={selectedEmployees.length === 0}
                        onClick={() => setShowEmailOptionsModal(true)}
                      >
                        <Mail className="h-4 w-4" />
                        Email Gönder
                      </Button>
                      <Button
                        variant="outline"
                        className="flex items-center gap-2 font-medium px-3"
                        disabled={selectedEmployees.length === 0}
                        onClick={() => setShowDueDateModal(true)}
                      >
                        <Clock className="h-4 w-4" />
                        Son Tarih Güncelle
                      </Button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2">
                    {/* Search input */}
                    <div className="relative col-span-1 md:col-span-2">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input
                        placeholder="Personel adı, sicil veya email ile ara..."
                        value={employeeSearchQuery}
                        onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                        className="pl-10 h-11 text-base"
                      />
                    </div>
                    <div className="col-span-1">
                      <Select value={emailStatusFilter} onValueChange={setEmailStatusFilter}>
                        <SelectTrigger className="h-11 text-base">
                          <SelectValue placeholder="Email Statüleri" />
                        </SelectTrigger>
                        <SelectContent>
                          {emailStatusFilterOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="col-span-1">
                      <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger className="h-11 text-base">
                          <SelectValue placeholder="Durumlar" />
                        </SelectTrigger>
                        <SelectContent>
                          {statusFilterOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <Checkbox
                            checked={
                              allFilteredChecked
                                ? true
                                : someFilteredChecked
                                  ? 'indeterminate'
                                  : false
                            }
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedEmployees((prev) =>
                                  Array.from(new Set([...prev, ...filteredIds]))
                                )
                              } else {
                                setSelectedEmployees((prev) =>
                                  prev.filter((id) => !filteredIds.includes(id))
                                )
                              }
                            }}
                          />
                        </TableHead>
                        <TableHead>Çalışan</TableHead>
                        <TableHead>Başlangıç Tarihi</TableHead>
                        <TableHead>Bitiş Tarihi</TableHead>
                        <TableHead>Email Durumu</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead>Erişim Kodu</TableHead>
                        <TableHead></TableHead> {/* New column for archive button */}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedAssignments.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedEmployees.includes(a.id)}
                              onCheckedChange={() => handleSelectEmployee(a.id)}
                            />
                          </TableCell>
                          <TableCell className="font-medium">{a.employee?.personel}</TableCell>
                          <TableCell className="text-sm">
                            {formatLocalDate(a.assignment_start_date)}
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatLocalDate(a.assignment_due_date)}
                          </TableCell>
                          <TableCell>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className={`flex items-center gap-1 ${getEmailStatusColor(a.email_status)}`}
                                  >
                                    {getEmailStatusIcon(a.email_status)}
                                    {getEmailStatusText(a.email_status)}
                                  </span>
                                </TooltipTrigger>
                              </Tooltip>
                            </TooltipProvider>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span
                                className={`flex items-center gap-1 ${getStatusColor(a.status)}`}
                              >
                                {getStatusIcon(a.status)}
                                {getStatusText(a.status)}
                              </span>
                              {(a.attempt_count || 1) > 1 && (
                                <span className="text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 px-1.5 py-0.5 rounded">
                                  D{a.attempt_count}/4
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">{a.pairing_code}</TableCell>
                          <TableCell>
                            {a.email_status === 'sent' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewEmailArchive(a.id)}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {/* Pagination */}
                  {totalAssignmentsPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAssignmentsPage((p) => Math.max(p - 1, 1))}
                        disabled={assignmentsPage === 1}
                      >
                        Önceki
                      </Button>
                      {getPaginationPages(assignmentsPage, totalAssignmentsPages).map(
                        (page, idx) =>
                          page === '...' ? (
                            <span key={'ellipsis-a-' + idx} className="px-2 text-gray-400">
                              ...
                            </span>
                          ) : (
                            <Button
                              key={'page-a-' + page}
                              variant={assignmentsPage === page ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setAssignmentsPage(page)}
                            >
                              {page}
                            </Button>
                          )
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setAssignmentsPage((p) => Math.min(p + 1, totalAssignmentsPages))
                        }
                        disabled={assignmentsPage === totalAssignmentsPages}
                      >
                        Sonraki
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
              {/* Email modals */}
              <EmailOptionsModal
                open={showEmailOptionsModal}
                onOpenChange={setShowEmailOptionsModal}
                selectedEmployeeCount={selectedEmployees.length}
                onProceed={handleEmailOptions}
                onSkip={() => setShowEmailOptionsModal(false)}
              />
              <EmailContentModal
                open={showEmailContentModal}
                onOpenChange={setShowEmailContentModal}
                onBack={() => setShowEmailContentModal(false)}
                testName={test.name}
                initialContent={composedEmail ?? undefined}
                onPreview={handleEmailPreview}
              />
              <EmailPreviewModal
                open={showEmailPreviewModal}
                onOpenChange={setShowEmailPreviewModal}
                onBack={handleEmailPreviewBack}
                onSend={handleSendEmails}
                content={emailContent!}
                test={test}
                selectedEmployeeCount={selectedEmployees.length}
              />
              <EmailSendingProgressModal
                open={showEmailProgressModal}
                onClose={handleCloseEmailProgress}
                progress={emailProgress}
              />
              <EmailArchiveModal
                open={showEmailArchiveModal}
                onOpenChange={setShowEmailArchiveModal}
                archive={selectedEmailArchive}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}

      <Dialog open={showDueDateModal} onOpenChange={setShowDueDateModal}>
        <DialogContent className="max-w-md w-full p-6 rounded-lg bg-white shadow-lg">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Son Tarihi Güncelle</DialogTitle>
          </DialogHeader>

          <div className="mt-4 space-y-4">
            <p className="text-sm text-gray-600">
              Seçili <b>{selectedEmployees.length}</b> çalışan için yeni son tarihi seçin:
            </p>

            <div className="flex flex-col space-y-2">
              <Label htmlFor="dueDate" className="text-sm font-medium text-gray-700">
                Sınav Bitiş Tarihi
              </Label>

              <DatePicker
                id="dueDate"
                selected={newDueDate}
                onChange={(date: Date | null) => setNewDueDate(date)}
                showTimeSelect
                timeFormat="HH:mm"
                dateFormat="yyyy-MM-dd HH:mm"
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-2">
            <button
              onClick={() => setShowDueDateModal(false)}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              İptal
            </button>
            <button
              onClick={handleUpdateDueDate}
              disabled={updatingDueDate || !newDueDate || selectedEmployees.length === 0}
              className={`px-4 py-2 rounded-md text-white ${
                updatingDueDate ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {updatingDueDate ? 'Güncelleniyor...' : 'Kaydet'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  )
}

export default AdminTestDetail
